import React, { useState, useContext, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TableSortLabel,
  Chip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { trackFeature } from '../utils/analytics';
import useIsMobile from '../utils/useIsMobile';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { doc, deleteDoc } from 'firebase/firestore';
import { parseISO, format, differenceInCalendarDays } from 'date-fns';
import { db } from '../firebaseConfig';
import { AppContext } from '../AppContext';
import AddTask from '../components/AddTask';
import ConfirmationDialog from '../components/ConfirmationDialog';
import TaskCalendar from '../components/TaskCalendar';
import { SnackbarContext } from '../SnackbarContext';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  '& .MuiTableSortLabel-root': {
    color: `${theme.palette.common.white} !important`,
    '&.Mui-active': {
      color: `${theme.palette.common.white} !important`,
    },
    '& .MuiTableSortLabel-icon': {
      color: `${theme.palette.common.white} !important`,
    },
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.75) !important',
    },
  },
}));

function Tasks() {
  // Check localStorage for saved tasks view setting on mount:
  const getInitialView = () => {
    const storedView = localStorage.getItem('tasksCalendarView');
    return storedView === null ? true : storedView === 'true';
  };

  const [calendarView, setCalendarView] = useState(getInitialView);

  useEffect(() => {
    localStorage.setItem('tasksCalendarView', calendarView);
  }, [calendarView]);

  // Get user, tasks, and userData from context.
  const { user, tasks, userData, farmData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const isMobile = useIsMobile();
  const { showSuccess, showError } = useContext(SnackbarContext);
  const defaultDateFormat = farmData?.settings?.dateFormat || 'dd-MM-yyyy';

  // Additional state for filtering.
  const [filterTaskType, setFilterTaskType] = useState('all');

  // Local states for Add/Edit dialog.
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);

  // Menu states for the context menu.
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  // Sorting state for tasks.
  const [tasksSortColumn, setTasksSortColumn] = useState('date');
  const [tasksSortDirection, setTasksSortDirection] = useState('asc');

  // Confirmation dialog states.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTask, setConfirmTask] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);

  const [tasksPage, setTasksPage] = useState(0);
  const [tasksRowsPerPage, setTasksRowsPerPage] = useState(10);

  const [selectedTasks, setSelectedTasks] = useState([]);

  // Filter tasks from context first:
  const sortedTasks = useMemo(() => {
    const data = [...tasks];
    data.sort((a, b) => {
      let aDate = a.date ? parseISO(a.date) : new Date(0);
      let bDate = b.date ? parseISO(b.date) : new Date(0);
      const dateCompare = tasksSortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      if (dateCompare !== 0) return dateCompare;
      const taskTypePriority = { sow: 1, lights: 2, water: 3, harvest: 4, delivery: 5 };
      const aPriority = taskTypePriority[a.type] || 99;
      const bPriority = taskTypePriority[b.type] || 99;
      return tasksSortDirection === 'asc' ? aPriority - bPriority : bPriority - aPriority;
    });
    return data;
  }, [tasks, tasksSortColumn, tasksSortDirection]);

  const filteredTasks = useMemo(() => {
    if (filterTaskType === 'all') {
      return sortedTasks;
    }
    return sortedTasks.filter((task) => task.type === filterTaskType);
  }, [sortedTasks, filterTaskType]);

  useEffect(() => {
    setTasksPage(0);
  }, [filterTaskType]);

  const paginatedTasks = useMemo(() => {
    const start = tasksPage * tasksRowsPerPage;
    return filteredTasks.slice(start, start + tasksRowsPerPage);
  }, [filteredTasks, tasksPage, tasksRowsPerPage]);


  // Other handlers for dialogs, edits, deletes, etc.
const handleOpen = () => {
  trackFeature('task_add_opened');
  setOpen(true);
};

  const handleCloseDialog = () => {
    setOpen(false);
    setEditMode(false);
    setTaskToEdit(null);
  };

  const handleAddTask = () => {
    handleCloseDialog();
  };

  const handleMenuOpen = (event, task) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedTask(null);
  };

const handleEdit = () => {
  if (selectedTask) {
    trackFeature('task_edit_opened', {
      id: selectedTask.id,
      type: selectedTask.type,
      label: selectedTask.label,
    });
  }
  setEditMode(true);
  setTaskToEdit(selectedTask);
  setOpen(true);
  handleMenuClose();
};

  const handleTasksSort = (column) => {
    const isAsc = tasksSortColumn === column && tasksSortDirection === 'asc';
    setTasksSortDirection(isAsc ? 'desc' : 'asc');
    setTasksSortColumn(column);
  };

  const openConfirmDialog = (taskOrGroup, actionType = 'complete') => {
    const group = Array.isArray(taskOrGroup) ? taskOrGroup : [taskOrGroup];
    setConfirmTask(group);
    setConfirmAction(actionType);
    setConfirmOpen(true);
  };

const handleConfirmCancel = () => {
  setConfirmOpen(false);
  setConfirmTask(null);
  setConfirmAction(null);
};


const handleConfirmAction = async () => {
  if (!user || !confirmTask?.length) return;
  if (!activeFarmId) {
    showError?.('No active farm selected.');
    return;
  }

  try {
    // 1) Delete or complete (you’re deleting in both cases)
    for (const task of confirmTask) {
      await deleteDoc(doc(db, 'farms', activeFarmId, 'tasks', task.id));
    }

    // 2) Fire the right event + success toast
    if (confirmAction === 'delete') {
      trackFeature('task_deleted', {
        count: confirmTask.length,
        taskIds: confirmTask.map((t) => t.id),
      });
      showSuccess?.(
        `${confirmTask.length} task${confirmTask.length > 1 ? 's' : ''} deleted.`
      );
    } else {
      trackFeature('task_completed', {
        count: confirmTask.length,
        taskIds: confirmTask.map((t) => t.id),
      });
      showSuccess?.(
        `${confirmTask.length} task${confirmTask.length > 1 ? 's' : ''} completed.`
      );
    }
  } catch (err) {
    // 3) Surface any errors
    if (confirmAction === 'delete') {
      showError?.('Error deleting task(s).');
    } else {
      showError?.('Error completing task(s).');
    }
  } finally {
    // 4) Clean up all flags
    setConfirmOpen(false);
    setConfirmTask(null);
    setConfirmAction(null);
  }
};

  const subscriptionActive = userData && userData.accessExpires
    ? new Date() <= (userData.accessExpires.toDate ? userData.accessExpires.toDate() : new Date(userData.accessExpires))
    : true;

  // Helper component for rendering the chip.
  const TaskTypeChip = ({ type, emoji, customColor, name }) => {
    let label = '';
    let paletteKey;
    switch (type) {
      case 'sow':
        label = 'Sow';
        paletteKey = 'success';
        break;
      case 'blackout':
        label = 'Blackout';
        paletteKey = 'quaternary';
        break;
      case 'lights':
        label = 'Under Lights';
        paletteKey = 'warning';
        break;
      case 'water':
        label = 'Water';
        paletteKey = 'primary';
        break;
      case 'harvest':
        label = 'Harvest';
        paletteKey = 'error';
        break;
      case 'delivery':
        label = 'Delivery';
        paletteKey = 'tertiary';
        break;
      case 'custom':
        label = name || 'Custom';
        break;
      default:
        label = type;
    }
    const taskEmojiMap = {
      sow: '🌱',
      blackout: '🕶️',
      lights: '💡',
      water: '💧',
      harvest: '✂️',
      delivery: '🚚',
    };
    const displayEmoji = emoji || taskEmojiMap[type] || '';
    const emojiComponent = (
      <span style={{ marginRight: '4px' }}>
        {displayEmoji}
      </span>
    );
    if (type === 'custom') {
      return (
        <Chip
          variant="filled"
          label={
            <>
              {emojiComponent}{label}
            </>
          }
          size="small"
          sx={{ backgroundColor: customColor || '#555555', color: 'white' }}
        />
      );
    }
    return (
      <Chip
        variant="filled"
        label={
          <>
            {emojiComponent}{label}
          </>
        }
        size="small"
        sx={(theme) => {
          const mapped = paletteKey ? theme.palette[paletteKey] : null;
          const bg = type === 'lights'
            ? '#d37e3d'
            : type === 'blackout'
              ? (mapped?.light || mapped?.main || theme.palette.grey[700])
              : (mapped?.dark || mapped?.main || theme.palette.grey[700]);
          return {
            backgroundColor: bg,
            color: theme.palette.getContrastText(bg),
          };
        }}
      />
    );
  };


  const count = Array.isArray(confirmTask) ? confirmTask.length : 0;
  const isSingle = (count === 1);



  return (
    <Box
      sx={{
        maxWidth: '80%',
        py: '3',
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '15px',
        '@media (max-width: 600px)': { maxWidth: '100%' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      {/* Header with segmented‐toggle and Add button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h4">Tasks</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={calendarView ? 'calendar' : 'list'}
            exclusive
            onChange={(_, val) => val !== null && setCalendarView(val === 'calendar')}
            size="small"
            aria-label="tasks view"
            sx={{
              borderRadius: '24px',
              overflow: 'hidden',
            }}
          >
            <ToggleButton
              value="calendar"
              aria-label="calendar view"
              sx={{
                px: 2.5,
                borderRadius: 0,
                '&:first-of-type': { borderRadius: '24px 0 0 24px' },
                '&:not(.Mui-selected)': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  color: (theme) => theme.palette.warning.main,
                  backgroundColor: 'transparent',
                },
              }}
            >
              <CalendarMonthIcon />
            </ToggleButton>

            <ToggleButton
              value="list"
              aria-label="list view"
              sx={{
                px: 2.5,
                borderRadius: 0,
                '&:last-of-type': { borderRadius: '0 24px 24px 0' },
                '&:not(.Mui-selected)': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  color: (theme) => theme.palette.warning.main,
                  backgroundColor: 'transparent',
                },
              }}
            >
              <ChecklistIcon />
            </ToggleButton>
          </ToggleButtonGroup>


          <Button
            variant="contained"
            color="secondary"
            onClick={handleOpen}
            sx={{ padding: '12px 24px' }}
            disabled={!subscriptionActive}
          >
            Add Task
          </Button>
        </Box>
      </Box>



      {/* Filter Control */}
      {!calendarView && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1">Filter by Task Type:</Typography>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={filterTaskType}
              onChange={(e) => setFilterTaskType(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="sow">Sow</MenuItem>
              <MenuItem value="lights">Lights</MenuItem>
              <MenuItem value="water">Water</MenuItem>
              <MenuItem value="harvest">Harvest</MenuItem>
              <MenuItem value="delivery">Delivery</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          {selectedTasks.length > 0 && (
            <Button
              color="tertiary"
              variant="contained"
              endIcon={<TaskAltIcon />}
              sx={{ 
                ml: '10px', padding: '8px 18px', 
                bgcolor: 'tertiary.dark',
                '&:hover': { bgcolor: 'tertiary.darker' },
              }}
              onClick={() => openConfirmDialog(
                tasks.filter(task => selectedTasks.includes(task.id)),
                'complete'
              )}
            >
              Complete Selected ({selectedTasks.length})
            </Button>
          )}
        </Box>
      )}


      {calendarView ? (
        <TaskCalendar
          tasks={sortedTasks}
          onTaskClick={(taskData) => {
            openConfirmDialog(taskData, 'complete');
          }}
        />
      ) : (
        <>
          {isMobile && (
            <Paper sx={{ mb: 2 }}>
              <List disablePadding>
                {paginatedTasks.map((task) => {
                  let displayDate = task.date;
                  let daysDiff = 0;
                  if (task.date) {
                    try {
                      const dateObj = parseISO(task.date);
                      displayDate = format(dateObj, defaultDateFormat);
                      daysDiff = differenceInCalendarDays(dateObj, new Date());
                    } catch (error) {
                      displayDate = task.date;
                    }
                  }
                  const chipColor = daysDiff < 0 ? 'warning' : daysDiff === 0 ? 'secondary' : 'default';
                  return (
                    <React.Fragment key={task.id}>
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => openConfirmDialog(task, 'complete')} sx={{ alignItems: 'flex-start' }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip size="small" color={chipColor} label={displayDate || 'No date'} />
                                <Typography variant="subtitle2">{task.label}</Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                  {task.type}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                  {task.notes || ''}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  );
                })}
                {paginatedTasks.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No tasks found." />
                  </ListItem>
                )}
              </List>
            </Paper>
          )}
        <TableContainer component={Paper} sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell padding="checkbox">
                  <Checkbox
                    label='Select All'
                    checked={selectedTasks.length === paginatedTasks.length}
                    indeterminate={
                      selectedTasks.length > 0 && selectedTasks.length < paginatedTasks.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTasks(paginatedTasks.map(task => task.id));
                      } else {
                        setSelectedTasks([]);
                      }
                    }}
                    sx={{
                      color: '#ffffff',
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                        color: '#ffffff',
                      },
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={tasksSortColumn === 'type'}
                    direction={tasksSortColumn === 'type' ? tasksSortDirection : 'asc'}
                    onClick={() => handleTasksSort('type')}
                  >
                    Task Type
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={tasksSortColumn === 'label'}
                    direction={tasksSortColumn === 'label' ? tasksSortDirection : 'asc'}
                    onClick={() => handleTasksSort('label')}
                  >
                    Description
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={tasksSortColumn === 'date'}
                    direction={tasksSortColumn === 'date' ? tasksSortDirection : 'asc'}
                    onClick={() => handleTasksSort('date')}
                  >
                    Due Date
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Complete</span>
                    <Tooltip
                      title={
                        <Typography sx={{ fontSize: '0.8rem' }}>
                          Completing task will remove it from both list and calendar view permanently.
                        </Typography>
                      }
                    >
                      <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                    </Tooltip>
                  </Box>
                </StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTasks.map((task) => {
                let displayDate = task.date;
                let daysDiff = 0;
                if (task.date) {
                  try {
                    const dateObj = parseISO(task.date);
                    displayDate = format(dateObj, defaultDateFormat);
                    daysDiff = differenceInCalendarDays(dateObj, new Date());
                  } catch (error) {
                    console.error('Error parsing date:', task.date, error);
                  }
                }
                return (
                  <TableRow
                    key={task.id}
                    sx={(theme) => ({
                      cursor: 'pointer',
                      backgroundColor:
                        daysDiff < 0
                          ? theme.palette.mode === 'dark'
                            ? theme.palette.warning.dark
                            : theme.palette.warning.lighter
                          : daysDiff === 0
                            ? theme.palette.mode === 'dark'
                              ? theme.palette.secondary.dark
                              : theme.palette.secondary.lighter
                            : 'inherit',
                    })}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedTasks.includes(task.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedTasks((prev) =>
                            checked ? [...prev, task.id] : prev.filter((id) => id !== task.id)
                          );
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <TaskTypeChip
                        type={task.type}
                        emoji={task.emoji}
                        customColor={task.color}
                        name={task.name}
                      />
                    </TableCell>
                    <TableCell>{task.label}</TableCell>
                    <TableCell>{displayDate}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="tertiary"
                        size="small"
                        endIcon={<TaskAltIcon />}
                        onClick={() => openConfirmDialog(task, 'complete')}
                        sx={{
                          bgcolor: 'tertiary.dark',
                          '&:hover': { bgcolor: 'tertiary.darker' },
                        }}
                      >
                        Complete
                      </Button>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={(e) => handleMenuOpen(e, task)}>
                        <SettingsIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {!calendarView && (
        <TablePagination
          component="div"
          count={filteredTasks.length}
          page={tasksPage}
          onPageChange={(e, newPage) => setTasksPage(newPage)}
          rowsPerPage={tasksRowsPerPage}
          onRowsPerPageChange={(e) => {
            setTasksRowsPerPage(parseInt(e.target.value, 10));
            setTasksPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
<MenuItem
  disabled={
    !subscriptionActive ||
    // disable if this task is tied to a harvest or an order
    Boolean(selectedTask?.harvestId) ||
    Boolean(selectedTask?.orderId)
  }
  onClick={handleEdit}
>
  Edit
</MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openConfirmDialog(selectedTask, 'delete');
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <AddTask
        open={open}
        onClose={handleCloseDialog}
        onAdd={handleAddTask}
        editMode={editMode}
        taskToEdit={taskToEdit}
        setSuccess={showSuccess}
        setError={showError}
      />

      <ConfirmationDialog
        open={confirmOpen}
        title={
          confirmAction === 'delete'
            ? `Delete ${isSingle ? 'Task' : 'Tasks'}`
            : `Mark ${isSingle ? 'Task' : 'Tasks'} as Complete`
        }
        message={
          confirmAction === 'delete'
            ? isSingle
              ? 'Are you sure you want to delete this task? This action cannot be undone.'
              : `Are you sure you want to delete ${count} tasks? This action cannot be undone.`
            : isSingle
              ? 'Mark this task as complete? It will be removed from your task list and calendar.'
              : `Mark ${count} tasks as complete? They’ll be removed from your task list and calendar.`
        }
        onConfirm={handleConfirmAction}
        onClose={handleConfirmCancel}
      />



    </Box>
  );
}

export default Tasks;

function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) {
    return true;
  }
  const now = new Date();
  const expires = userData.accessExpires.toDate
    ? userData.accessExpires.toDate()
    : new Date(userData.accessExpires);
  return now <= expires;
}



